import * as React from 'react';
import { useAuth } from '@/hooks/useAuth';

interface UseStripePublicGateReturn {
  /** Invoke when the user selects Public; runs onProceed if Stripe is active, else opens the warning. */
  trigger: () => void;
  /** True while the "No Earnings Without Stripe" warning is open. */
  isWarningOpen: boolean;
  /** Close the warning without proceeding. */
  closeWarning: () => void;
}

/**
 * Gates a "go public" action behind Stripe Connect status.
 * Consumers render <StripeGateModal> with { isOpen: isWarningOpen, onClose: closeWarning, onProceed }.
 *
 * @param {() => void} onProceed - Action to run when the user confirms going public (directly or via OK in the warning).
 * @returns {UseStripePublicGateReturn}
 */
export function useStripePublicGate(onProceed: () => void): UseStripePublicGateReturn {
  const { user } = useAuth();
  const [isWarningOpen, setIsWarningOpen] = React.useState(false);

  const isStripeActive = user?.stripe_account_status === 'active';

  const trigger = React.useCallback(() => {
    if (isStripeActive) {
      onProceed();
    } else {
      setIsWarningOpen(true);
    }
  }, [isStripeActive, onProceed]);

  const closeWarning = React.useCallback(() => setIsWarningOpen(false), []);

  return { trigger, isWarningOpen, closeWarning };
}
